import React, { useEffect, useState } from "react";
import { UserMfaCell } from "../components/UserMfaCell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { UserPlus, Trash2, Loader2, ShieldCheck, Edit3, MailPlus, RotateCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { Navigate } from "react-router-dom";

const ROLE_COLORS = {
  super_admin: "bg-amber-600 text-white border-amber-600",
  admin: "bg-slate-900 text-white border-slate-900",
  manager: "bg-emerald-700 text-white border-emerald-700",
  editor: "bg-sky-700 text-white border-sky-700",
  viewer: "bg-slate-200 text-slate-700 border-slate-300",
};

const ROLES = ["admin", "manager", "editor", "viewer"];
const ROLE_DESCRIPTIONS = {
  admin: "Full access — manage users, properties, alerts, settings",
  manager: "Read/write all data, import CSVs, edit settings (no user mgmt)",
  editor: "Run checks, mark alerts read, edit properties (no import/settings)",
  viewer: "Read-only access to dashboard, properties, and alerts",
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resending, setResending] = useState({});

  const isAdmin = currentUser && currentUser !== false && ["super_admin", "admin"].includes(currentUser.role);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line

  if (currentUser && currentUser !== false && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-8 space-y-6 fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Access control</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">Users & Roles</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage who can access Housing Intelligence and what they can do.</p>
          </div>
          <div className="flex gap-2">
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-none border-slate-300" data-testid="open-create-user">
              <UserPlus className="h-4 w-4 mr-2" /> Add user
            </Button>
          </DialogTrigger>
          <CreateUserDialog onCreated={() => { setShowCreate(false); load(); }} />
        </Dialog>
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogTrigger asChild>
            <Button className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="open-invite-user">
              <MailPlus className="h-4 w-4 mr-2" /> Invite by email
            </Button>
          </DialogTrigger>
          <InviteUserDialog onInvited={() => { setShowInvite(false); load(); }} />
        </Dialog>
          </div>
      </div>

      {/* Roles legend */}
      <Card className="rounded-none border-border p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Role capabilities</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROLES.map((r) => (
            <div key={r} className="border border-border p-4">
              <Badge className={`rounded-none ${ROLE_COLORS[r]}`}>{r.toUpperCase()}</Badge>
              <div className="text-xs text-muted-foreground mt-2 leading-relaxed">{ROLE_DESCRIPTIONS[r]}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Users table */}
      <Card className="rounded-none border-border overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="border-border">
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Email</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Role</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">MFA</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700">Created</TableHead>
              <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-700 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6" /></TableCell></TableRow>
              ))
            ) : users.map((u) => (
              <TableRow key={u.id} className="hover:bg-slate-50 border-border" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  {u.name}
                  {u.id === currentUser?.id && <span className="text-xs text-muted-foreground">(you)</span>}
                  {u.pending_invite && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border" style={{ backgroundColor: "#fff8ed", borderColor: "var(--hi-orange)", color: "var(--hi-orange-deep)" }} data-testid={`pending-invite-${u.id}`}>
                      <Clock className="h-2.5 w-2.5 inline mr-0.5" /> Pending
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell>
                  <Badge className={`rounded-none ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                    {u.role.replace("_", " ").toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell><UserMfaCell u={u} currentUser={currentUser} onChanged={load} /></TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {u.pending_invite && (
                      <Button
                        variant="ghost" size="sm" className="rounded-none h-7"
                        disabled={!!resending[u.id]}
                        onClick={async () => {
                          setResending((s) => ({ ...s, [u.id]: true }));
                          try {
                            const { data } = await api.post(`/users/${u.id}/resend-invite`);
                            toast.success(`Invite resent (${data.email_provider !== "mock" ? "live" : "mock"})`, { description: data.accept_url });
                            load();
                          } catch (e) {
                            toast.error(formatApiError(e?.response?.data?.detail));
                          } finally {
                            setResending((s) => ({ ...s, [u.id]: false }));
                          }
                        }}
                        data-testid={`resend-invite-${u.id}`}
                        title="Resend invitation email"
                      >
                        {resending[u.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm" className="rounded-none h-7"
                      onClick={() => setEditingUser(u)}
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="rounded-none h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={u.id === currentUser?.id}
                      onClick={() => setDeletingUser(u)}
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        {editingUser && (
          <EditUserDialog
            user={editingUser}
            onSaved={() => { setEditingUser(null); load(); }}
          />
        )}
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deletingUser} onOpenChange={(o) => !o && setDeletingUser(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{deletingUser?.email}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="cancel-delete-user">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
              onClick={async () => {
                try {
                  await api.delete(`/users/${deletingUser.id}`);
                  toast.success("User deleted");
                  load();
                } catch (e) {
                  toast.error(formatApiError(e?.response?.data?.detail));
                } finally {
                  setDeletingUser(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateUserDialog({ onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="rounded-none">
      <DialogHeader>
        <DialogTitle>Add user</DialogTitle>
        <DialogDescription>New users can sign in immediately with the email + password you set here.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4" data-testid="create-user-form">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-xs uppercase tracking-wider font-semibold">Name</Label>
          <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            required className="rounded-none border-slate-300" data-testid="new-user-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold">Email</Label>
          <Input id="email" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required className="rounded-none border-slate-300" data-testid="new-user-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold">Password (min 6 chars)</Label>
          <Input id="password" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required minLength={6} className="rounded-none border-slate-300" data-testid="new-user-password" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="rounded-none border-slate-300" data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[form.role]}</div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="submit-new-user">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Create user
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditUserDialog({ user, onSaved }) {
  const [form, setForm] = useState({ name: user.name, role: user.role, password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name };
      if (user.role !== "super_admin") payload.role = form.role;
      if (form.password) payload.password = form.password;
      await api.put(`/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="rounded-none">
      <DialogHeader>
        <DialogTitle>Edit user</DialogTitle>
        <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4" data-testid="edit-user-form">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            required className="rounded-none border-slate-300" data-testid="edit-user-name" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">Role</Label>
          {user.role === "super_admin" ? (
            <div className="text-sm border border-slate-300 px-3 py-2 bg-slate-50" data-testid="edit-user-role-locked">
              Super Admin <span className="text-xs text-muted-foreground">· fixed, can't be changed here</span>
            </div>
          ) : (
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none border-slate-300" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">New password (optional)</Label>
          <Input type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Leave blank to keep current" className="rounded-none border-slate-300" data-testid="edit-user-password" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="submit-edit-user">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save changes
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


function InviteUserDialog({ onInvited }) {
  const [form, setForm] = useState({ email: "", name: "", role: "editor" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/users/invite", form);
      setResult(data.invite);
      toast.success(`Invitation sent (${data.invite.email_provider === "resend" ? "live email" : "mock email"})`, {
        description: data.invite.email_status === "sent" ? "User will receive the invite shortly" : "Email send failed — share the link manually",
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="rounded-none">
      <DialogHeader>
        <DialogTitle>Invite by email</DialogTitle>
        <DialogDescription>
          Send a 7-day invite link. The new user picks their own password on first sign-in.
        </DialogDescription>
      </DialogHeader>
      {result ? (
        <div className="space-y-3" data-testid="invite-result">
          <div className="border border-emerald-300 bg-emerald-50 p-3 text-xs">
            <div className="font-bold text-emerald-900">Invitation created ✓</div>
            <div className="text-emerald-800/80 mt-1">
              Email provider: <span className="font-mono">{result.email_provider}</span> · status: <span className="font-mono">{result.email_status}</span>
            </div>
          </div>
          <div className="text-xs">
            <div className="text-muted-foreground mb-1">Accept-invite link (copy if email failed):</div>
            <code className="block bg-slate-100 p-2 break-all text-[10px] font-mono">{result.accept_url}</code>
          </div>
          <DialogFooter>
            <Button onClick={onInvited} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="invite-done">Done</Button>
          </DialogFooter>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="invite-user-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none border-slate-300" data-testid="invite-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-none border-slate-300" data-testid="invite-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none border-slate-300" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[form.role]}</div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="submit-invite">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailPlus className="h-4 w-4 mr-2" />} Send invitation
            </Button>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  );
}
